"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";

export type CityMarker = {
  slug: string;
  name: string;
  phone: string;
  address: string;
  url?: string;
  lat: number;
  lng: number;
};

declare global {
  interface Window {
    ymaps?: {
      ready: (callback: () => void) => void;
      Map: new (
        container: HTMLElement | string,
        state: { center: [number, number]; zoom: number; controls?: string[] },
        options?: { suppressMapOpenBlock?: boolean }
      ) => YandexMapInstance;
      Placemark: new (
        coords: [number, number],
        properties: { balloonContent?: string; hintContent?: string },
        options?: Record<string, unknown>
      ) => unknown;
      GeoObjectCollection: new () => {
        add: (obj: unknown) => void;
        getBounds: () => [[number, number], [number, number]];
      };
    };
  }
}

type YandexMapInstance = {
  geoObjects: {
    add: (obj: unknown) => void;
    getBounds: () => [[number, number], [number, number]] | null;
  };
  setBounds: (
    bounds: [[number, number], [number, number]],
    options?: { checkZoomRange?: boolean; zoomMargin?: number }
  ) => Promise<unknown> | void;
  destroy: () => void;
};

/**
 * Yandex Maps с точками-маркерами по городам. При клике на маркер —
 * балун с названием города, адресом, телефоном и ссылкой на городской сайт.
 *
 * Без API-ключа карта работает с пометкой Yandex; для прод-сайта ключ
 * нужно положить в NEXT_PUBLIC_YANDEX_MAPS_KEY (опционально).
 */
export default function YandexMap({ cities, apiKey }: { cities: CityMarker[]; apiKey?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<YandexMapInstance | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ready || !containerRef.current || mapRef.current) return;
    if (!window.ymaps) return;

    window.ymaps.ready(() => {
      if (!containerRef.current || !window.ymaps) return;

      const map = new window.ymaps.Map(
        containerRef.current,
        {
          // Центрируем приблизительно на Россию; setBounds потом подгонит
          // под фактический набор точек.
          center: [60, 90],
          zoom: 3,
          controls: ["zoomControl", "fullscreenControl"],
        },
        { suppressMapOpenBlock: true }
      );

      const collection = new window.ymaps.GeoObjectCollection();
      cities.forEach((city) => {
        const balloon = `
          <div style="font-family: system-ui, sans-serif; padding: 4px 2px; min-width: 200px;">
            <div style="font-size: 16px; font-weight: 600; color: #010205; margin-bottom: 6px;">${escapeHtml(city.name)}</div>
            <div style="font-size: 13px; color: #444; line-height: 1.45; margin-bottom: 8px;">${escapeHtml(city.address)}</div>
            <div style="font-size: 13px; margin-bottom: 6px;"><a href="tel:${city.phone.replace(/[^\d+]/g, "")}" style="color: #0033A1; text-decoration: none;">${escapeHtml(city.phone)}</a></div>
            ${city.url ? `<a href="${escapeHtml(city.url)}" target="_blank" rel="noopener" style="display: inline-block; padding: 6px 12px; background: #0033A1; color: white; border-radius: 14px; font-size: 12px; text-decoration: none; margin-top: 4px;">Открыть сайт →</a>` : ""}
          </div>`;
        const placemark = new window.ymaps!.Placemark(
          [city.lat, city.lng],
          { balloonContent: balloon, hintContent: city.name },
          { preset: "islands#blueDotIconWithCaption", iconCaption: city.name }
        );
        collection.add(placemark);
      });
      map.geoObjects.add(collection);

      // Подгоняем карту так, чтобы влезли все точки (с отступом).
      const bounds = collection.getBounds();
      if (bounds) {
        map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 });
      }

      mapRef.current = map;
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, [ready, cities]);

  // Ключ — из admin-настроек (prop), с fallback на env.
  const key = apiKey || process.env.NEXT_PUBLIC_YANDEX_MAPS_KEY;
  const apiUrl = key
    ? `https://api-maps.yandex.ru/2.1/?apikey=${key}&lang=ru_RU`
    : `https://api-maps.yandex.ru/2.1/?lang=ru_RU`;

  return (
    <>
      <Script src={apiUrl} strategy="afterInteractive" onLoad={() => setReady(true)} />
      <div ref={containerRef} className="h-full w-full" />
    </>
  );
}

// Простая HTML-санитизация для содержимого балуна — экранирует все символы,
// которые могут сломать вложенный HTML или дать XSS, если в name/address
// случайно окажется <script>.
function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
